import * as THREE from "three";

export default class Galaxy {
  constructor() {
    this.mesh = new THREE.Object3D();

    // Enhanced settings for a double spiral and planar galaxy
    const galaxyRadius = 40;
    const galaxyThickness = 6; // Reduced thickness for a more planar look
    const galaxyTurns = 3; // Adjusted turns
    const numStars = 30000; // Increased star count for better definition
    const starSizeBase = 0.1;
    const starSizeRandomness = 0.07;
    const spiralTightness = 0.5;
    const centralBulgeRadius = 10;
    const bulgeFactor = 2;
    const armDensityFactor = 1.5;

    this.geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(numStars * 3);
    const colors = new Float32Array(numStars * 3);
    const sizes = new Float32Array(numStars);

    for (let i = 0; i < numStars; i++) {
      const i3 = i * 3;

      // Determine which arm the star belongs to (0 or 1)
      const arm = Math.random() < 0.5 ? 0 : 1;
      const phaseOffset = arm * Math.PI; // Offset for the second arm

      // Position within the galaxy structure
      const t = Math.random();
      const angle = t * Math.PI * 2 * galaxyTurns + phaseOffset;
      const baseRadius = galaxyRadius * t * spiralTightness;

      // More pronounced and smoother spiral arms
      const armOffset = Math.sin(angle * 2.5) * 2.5; // Adjusted frequency and amplitude
      const currentRadius = Math.max(0, baseRadius + armOffset + (Math.random() * 2 - 1)); // Reduced radial variation

      const yOffset = (Math.random() - 0.5) * galaxyThickness * 0.6; // More planar

      const x = Math.cos(angle) * currentRadius + (Math.random() - 0.5) * 1;
      const z = Math.sin(angle) * currentRadius + (Math.random() - 0.5) * 1;

      // Central bulge with higher density
      const distanceToCenter = Math.sqrt(x * x + z * z);
      let finalX = x;
      let finalY = yOffset;
      let finalZ = z;

      if (distanceToCenter < centralBulgeRadius) {
        const bulgeRandomness = centralBulgeRadius - distanceToCenter;
        finalX += (Math.random() - 0.5) * bulgeRandomness * bulgeFactor;
        finalY += (Math.random() - 0.5) * bulgeRandomness * bulgeFactor * 0.4;
        finalZ += (Math.random() - 0.5) * bulgeRandomness * bulgeFactor;
      } else {
        const armStrength = Math.sin(angle * 2.5) * 0.5 + 0.5;
        if (Math.random() > 0.4 * (1 - armStrength * armDensityFactor)) {
          finalX += (Math.random() - 0.5) * 0.8;
          finalZ += (Math.random() - 0.5) * 0.8;
        } else {
          finalX += (Math.random() - 0.5) * 0.4;
          finalZ += (Math.random() - 0.5) * 0.4;
        }
      }

      positions[i3] = finalX;
      positions[i3 + 1] = finalY;
      positions[i3 + 2] = finalZ;

      // Brighter Color (blue, purple, white glow)
      const color = new THREE.Color();
      const normalizedRadius = currentRadius / galaxyRadius;
      const randomColor = Math.random();

      if (normalizedRadius < 0.3) {
        if (randomColor < 0.5) {
          color.setHSL(0.7 + Math.random() * 0.1, 0.7 + Math.random() * 0.2, 0.7 + Math.random() * 0.3);
        } else {
          color.setHSL(0, 0, 0.8 + Math.random() * 0.2);
        }
      } else if (randomColor < 0.4) {
        color.setHSL(0.6 + Math.random() * 0.1, 0.9 + Math.random() * 0.1, 0.6 + Math.random() * 0.2);
      } else if (randomColor < 0.7) {
        color.setHSL(0.75 + Math.random() * 0.05, 0.7 + Math.random() * 0.2, 0.5 + Math.random() * 0.2);
      } else {
        color.setHSL(0, 0, 0.7 + Math.random() * 0.2);
      }

      colors[i3] = color.r;
      colors[i3 + 1] = color.g;
      colors[i3 + 2] = color.b;

      // Size
      sizes[i] = starSizeBase + Math.random() * starSizeRandomness;
    }

    this.geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    this.geometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1));

    // Material for the stars
    this.material = new THREE.PointsMaterial({
      size: starSizeBase * 1.5,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    // Points object
    this.points = new THREE.Points(this.geometry, this.material);
    this.mesh.add(this.points);

    // No initial rotation to keep it planar (XY-plane by default)
    // this.mesh.rotation.x = Math.PI / 2; // Removed this line
  }

  update() {
    this.mesh.rotation.z += 0.0005;
  }
}